import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PlayIcon, ShieldCheckIcon, CheckIcon, LockIcon } from 'lucide-react';
import { TrustedBy } from '../components/enterprise/TrustedBy';
import { StackCarousel } from '../components/enterprise/StackCarousel';
import { PlatformBento } from '../components/enterprise/PlatformBento';
import { GuidedStart } from '../components/enterprise/GuidedStart';
import { TALK_TO_SALES_URL } from '../lib/links';

// Both "Talk to sales" actions on this page point at the shared booking link,
// so the label means the same thing here as it does in the site header.
const CALENDLY = TALK_TO_SALES_URL;

/* Access control panel used in the hero. A model picker would only say "we
 have models"; for an enterprise buyer the screen that matters is the one
 answering "who can reach what", so the hero shows group-level access. */

const GROUPS = [{
  name: 'Everyone',
  members: 1240,
  tools: ['Slack', 'Google Drive', 'Confluence'],
  scope: 'Read-only knowledge'
}, {
  name: 'Revenue',
  members: 186,
  tools: ['Salesforce', 'Gong', 'HubSpot', 'Slack'],
  scope: 'Read and write'
}, {
  name: 'Engineering',
  members: 342,
  tools: ['GitHub', 'Linear', 'Sentry', 'Slack'],
  scope: 'Read and write'
}, {
  name: 'Finance',
  members: 41,
  tools: ['NetSuite', 'Ramp', 'Google Sheets'],
  scope: 'Restricted · approval required'
}];
function AccessPanel() {
  const [active, setActive] = useState(1);
  const group = GROUPS[active];
  return <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_70px_-25px_rgba(43,33,26,0.3)]" data-id="element-9709" data-mp="1sws96wotge4e">
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-[#FBFAF8] px-4 py-3" data-id="element-9710" data-mp="1xrfpw9dkw48f">
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" data-id="element-9711" data-mp="22q0ok4d6mfur" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" data-id="element-9712" data-mp="27ou6c8zi4t0q" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" data-id="element-9713" data-mp="2bo2yxait2kup" />
        <span className="ml-3 text-[12px] font-semibold text-neutral-400" data-id="element-9714" data-mp="ck47vdhvqp61">
          Admin · Access control
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-id="element-9715" data-mp="7lt0d9f24qqz">
        <div className="border-b border-neutral-200 sm:border-b-0 sm:border-r" data-id="element-9716" data-mp="lcy3ilhmw162">
          <p className="px-5 pb-3 pt-5 text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-9717" data-mp="glpp1b1g5pm1">
            Groups
          </p>
          <ul data-id="element-9718" data-mp="v9au3ahvtywl">
            {GROUPS.map((g, i) => <li key={g.name} data-id="element-9719" data-mp="vcf5osxofwxa" data-mpi={i}>
                <button type="button" onClick={() => setActive(i)} aria-pressed={i === active} className={`flex w-full items-center justify-between px-5 py-3 text-left transition-colors ${i === active ? 'bg-[#FFF7F0]' : 'hover:bg-neutral-50'}`} data-id="element-9720" data-mp="qc0x99s5ykyq">
                  <span className="flex items-center gap-2.5" data-id="element-9721" data-mp="10agn79fgfo6n">
                    <span className={`h-1.5 w-1.5 rounded-full ${i === active ? 'bg-[#EA580C]' : 'bg-neutral-300'}`} data-id="element-9722" data-mp="361vl7j93dh6d" />
                    <span className={`text-sm ${i === active ? 'font-bold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-9723" data-mp="322d7p8jlo8dk">
                      {g.name}
                    </span>
                  </span>
                  <span className="text-[12px] tabular-nums text-neutral-400" data-id="element-9724" data-mp="2vy72i54uy5uy">
                    {g.members.toLocaleString()}
                  </span>
                </button>
              </li>)}
          </ul>
        </div>

        <div className="p-5 pb-16" data-id="element-9725" data-mp="2n4232xbnrm3t">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-9726" data-mp="288mdp5m0wedr">
            {group.name} can reach
          </p>
          <ul className="mt-3 space-y-2" data-id="element-9727" data-mp="2d9iidle784k8">
            {group.tools.map(t => <li key={t} className="flex items-center gap-2.5 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-9728" data-mp="1z97o08dok7ub">
                <CheckIcon size={14} strokeWidth={3} className="shrink-0 text-[#EA580C]" data-id="element-9729" data-mp="249n4h8t9dvgi" />
                {t}
              </li>)}
          </ul>
          <div className="mt-4 flex items-center gap-2 border-t border-neutral-200 pt-4 text-[12.5px] text-neutral-500" data-id="element-9730" data-mp="2gfpk0hk5fv5">
            <LockIcon size={13} className="shrink-0" data-id="element-9731" data-mp="1yhzm1pefyb6s" />
            {group.scope}
          </div>
        </div>
      </div>
    </div>;
}
export function EnterprisePlatform() {
  return <div className="min-h-screen bg-[#F4F3F1] font-body text-[#2B211A]" data-id="element-9732" data-mp="1okd3msxxo1i2">
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="px-6 pt-28 pb-4 md:px-10 md:pt-36" data-id="element-9733" data-mp="1klm289mfpxp5">
        <div className="mx-auto max-w-[820px] text-center" data-id="element-9734" data-mp="1fl85hnqaaa6f">
          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="font-title text-4xl font-medium leading-[1.02] tracking-tight md:text-6xl lg:text-[72px]" data-id="element-9735" data-mp="1atzvoql1kmsq">
            The Platform for AI Adoption
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.06,
          ease: [0.23, 1, 0.32, 1]
        }} className="mx-auto mt-6 max-w-[520px] text-lg leading-relaxed text-neutral-500 md:text-xl" data-id="element-9736" data-mp="ruvn1ixewv1l">
            Giga lets you roll AI out across your entire organisation, in a
            safe and flexible way.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.12,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-9737" data-mp="3hh761qxrx3z6">
            <a href="https://app.getgiga.com/signup" className="inline-flex w-full items-center justify-center rounded-full bg-[#2B211A] px-7 py-3.5 font-bold text-white transition-colors hover:bg-[#3d2f25] sm:w-auto" data-id="element-9738" data-mp="2yj4eb4ducsxo">
              Start free
            </a>
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center rounded-full border border-[#2B211A]/15 bg-white px-7 py-3.5 font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]/30 sm:w-auto" data-id="element-9739" data-mp="2618r56fwznq1">
              Talk to sales
            </a>
          </motion.div>
        </div>

        {/* Product panel with the tour pill sitting over it. */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative mx-auto mt-16 max-w-[1000px]" data-id="element-9740" data-mp="3e6k39nj5soxu">
          <AccessPanel data-id="element-9741" data-mp="2zh4eue73ovlb" />
          <Link to="/demo" className="group absolute bottom-5 left-1/2 inline-flex -translate-x-1/2 items-center gap-3 rounded-full bg-[#2B211A]/90 py-2.5 pl-5 pr-2.5 text-white backdrop-blur-sm transition-colors hover:bg-[#2B211A]" data-id="element-9742" data-mp="2zd6wvvw9ea30">
            <span className="text-left" data-id="element-9743" data-mp="9r0uaps9qbxr">
              <span className="block text-sm font-bold leading-tight" data-id="element-9744" data-mp="9pziuk7cbnfi">
                Watch product tour
              </span>
              <span className="block text-[12px] leading-tight text-white/60" data-id="element-9745" data-mp="m8v52lrrj0cu">
                4 minutes
              </span>
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition-colors group-hover:bg-white/25" data-id="element-9746" data-mp="8a3tp74absse">
              <PlayIcon size={13} className="translate-x-[1px] fill-white text-white" data-id="element-9747" data-mp="da9ga8vp6p3m" />
            </span>
          </Link>
        </motion.div>
      </section>

      <TrustedBy data-id="element-9748" data-mp="15vstux3rw0lr" />

      <StackCarousel data-id="element-9749" data-mp="15wb08mk5fv7e" />

      <PlatformBento data-id="element-9750" data-mp="15yneaomsdlf6" />

      <GuidedStart data-id="element-9751" data-mp="r87oewjfdpao" />

      {/* ── Closing ────────────────────────────────────────────────────── */}
      <section className="px-6 py-20 md:px-10 md:py-28" data-id="element-9752" data-mp="w6hn3h4cz811">
        <div className="mx-auto max-w-[1100px] rounded-2xl bg-[#2B211A] px-8 py-16 text-center text-white md:px-16 md:py-20" data-id="element-9753" data-mp="w56gzanfxo6r">
          <h2 className="mx-auto max-w-[16ch] font-title text-3xl font-medium leading-tight tracking-tight md:text-5xl" data-id="element-9754" data-mp="1onbvazi5f6lo">
            Start with one team. Plan for all of them.
          </h2>
          <p className="mx-auto mt-5 max-w-[500px] text-lg leading-relaxed text-white/60" data-id="element-9755" data-mp="3ksb4xlp5b0w3">
            We will map your first rollout, the systems it touches, and the
            controls your security review will ask for.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-9756" data-mp="36zv1wkw8v2v6">
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center rounded-full bg-[#EA580C] px-7 py-3.5 font-bold text-white transition-colors hover:bg-[#C2410C] sm:w-auto" data-id="element-9757" data-mp="7k6qg9jmwdp4">
              Talk to sales
            </a>
            <Link to="/unlisted/security" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-bold text-white transition-colors hover:border-white/40 sm:w-auto" data-id="element-9758" data-mp="3pptgzkyglw8b">
              <ShieldCheckIcon size={17} strokeWidth={2} data-id="element-9759" data-mp="2e9cy1hnp4xgp" />
              Security overview
            </Link>
          </div>
        </div>
      </section>
    </div>;
}